'use client';

// Imports the global styles for the site
import { ApolloProvider, gql, useQuery } from '@apollo/client';
import {
  type ClientAccessRequirement,
  mondayOf,
  NO_PERMISSION_ARM,
  type NonEmptyPermissions,
  type PermissionKey,
  UNASSIGNED_CLIENTS_FULL_KEY,
} from '@kg-internal/pv3-helpers';
import dayjs from 'dayjs';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import React, { createContext, JSX, useContext, useEffect, useState } from 'react';
import Commander from '../../components/Commander/Commander';
import ErrorDisplay from '../../components/ErrorDisplay/ErrorDisplay';
import LogoLoader from '../../components/LogoLoader/LogoLoader';
import NoPermission from '../../components/NoPermission/NoPermission';
import config, { PortalConfigType } from '../../config';
// import '../../styles/global.scss';
import graphClient, { baseLink, graphClientPersisted, persistedLink, simpleClient, simpleLink } from './graphClient';
import { getClosestOffice, getCurrentLocation, locations, TLocation } from './locationsUtil';
import PortalErrorBoundary from './PortalErrorBoundary';
import userObj, { IUserObj } from './userObj';

type envType = 'production' | 'development' | 'staging' | null;

// Redirect to login functions
const redirectToLogin = (env: envType) => {
  const { loginUrl } = env ? (config.urls[env] ?? config.urls) : config.urls;
  // Component-encoded so the page's own `&`, `#` and `+` stay inside returnUrl, and its escapes survive auth's one decode.
  const prevLocation = encodeURIComponent(window.location.href);

  // ensure loading screen is visible before redirect occurs
  setTimeout(() => {
    console.info(`redirecting to: ${loginUrl}/login?returnUrl=${prevLocation}`);
    window.location.href = `${loginUrl}/login?returnUrl=${prevLocation}`;
  }, 5);
};

// Redirect to handbook because the person has updates
const redirectToHandbook = (env: envType) => {
  const { handbook } = env ? (config.urls[env] ?? config.urls) : config.urls;
  const prevLocation = encodeURI(window.location.href);

  // ensure loading screen is visible before redirect occurs
  setTimeout(() => {
    window.location.href = `${handbook}/?returnUrl=${prevLocation}`;
  }, 5);
};

const redirectToEditProfile = (env: envType, id: number) => {
  const { directory } = env ? (config.urls[env] ?? config.urls) : config.urls;

  // ensure loading screen is visible before redirect occurs
  setTimeout(() => {
    window.location.href = `${directory}/profile/${id}/edit`;
  }, 5);
};
// Force a person with an overdue timesheet to the week they owe. The legacy timetracker app
// forwards this `:date` param on to Polaris for anyone holding `app.polaris`, so the week has to
// stay in the URL — without it they land on the current week instead of the overdue one.
const redirectToTimesheet = (env: envType) => {
  const { timetracker } = env ? (config.urls[env] ?? config.urls) : config.urls;

  const lastWeek = mondayOf().subtract(1, 'week').format('YYYY-MM-DD');

  // ensure loading screen is visible before redirect occurs
  setTimeout(() => {
    window.location.href = `${timetracker}/week/${lastWeek}?incomplete=true`;
  }, 5);
};

interface IApolloContextLite {
  children: React.ReactNode | Array<React.ReactNode>;
  name: string;
  version: string;
  persistedQueries?: boolean;
  simple?: boolean;
}

// Apollo context to provide API access
export const ApolloContextLite = (props: IApolloContextLite) => {
  const { children, name, version, simple = false, persistedQueries = true } = props;

  if (name) {
    // @ts-expect-error there is no other way to access this property
    graphClient.queryManager.clientAwareness.name = name;
  }

  if (version) {
    // @ts-expect-error there is no other way to access this property
    graphClient.queryManager.clientAwareness.version = version;
  }

  if (simple) {
    graphClient.link = simpleLink;
  } else if (persistedQueries) {
    graphClient.link = persistedLink;
  } else {
    graphClient.link = baseLink;
  }

  return <ApolloProvider client={graphClient}>{children}</ApolloProvider>;
};

interface IApolloContext extends IPortalContext {
  children: React.ReactNode | Array<React.ReactNode>;
  name: string;
  version: string;
  persistedQueries?: boolean;
  simple?: boolean;
}

// Apollo context rto provide API access
const ApolloContext = (props: IApolloContext) => {
  const { children, name, version, simple = false, persistedQueries = true, ...otherProps } = props;

  if (name) {
    // @ts-expect-error there is no other way to access this property
    simpleClient.queryManager.clientAwareness.name = name;
    // @ts-expect-error there is no other way to access this property
    graphClient.queryManager.clientAwareness.name = name;
    // @ts-expect-error there is no other way to access this property
    graphClientPersisted.queryManager.clientAwareness.name = name;
  }

  if (version) {
    // @ts-expect-error there is no other way to access this property
    simpleClient.queryManager.clientAwareness.version = version;
    // @ts-expect-error there is no other way to access this property
    graphClient.queryManager.clientAwareness.version = version;
    // @ts-expect-error there is no other way to access this property
    graphClientPersisted.queryManager.clientAwareness.version = version;
  }

  return (
    <ApolloProvider client={simple ? simpleClient : persistedQueries ? graphClientPersisted : graphClient}>
      <PostHogProvider
        apiKey={
          props.env === 'production'
            ? 'phc_70C1xiKrZbavrOEBgDLsgfTJIkEPjrhEnt5ZvhGZxLQ' // prod
            : 'phc_MzEfTBvkPfqsrIo3tFTDGyqWdNh8bVl2PxetPiJiCX5' // stage
        }
        options={{ api_host: 'https://app.posthog.com' }}>
        <PortalErrorBoundary>
          <PortalContext {...otherProps} name={name} version={version}>
            {children}
          </PortalContext>
        </PortalErrorBoundary>
      </PostHogProvider>
    </ApolloProvider>
  );
};

// Base context so we can layer in user details
const BaseCtx = createContext({} as IUserObj);

const USER_ME = gql`
  query PortalContextMe {
    # fresh: this response decides three full-page redirects, and the cached profile can be up to a
    # minute behind the row across graph instances — long enough to send someone back to a page they
    # have just finished with. Nothing else asks for it.
    me(fresh: true) {
      id
      email
      nameFirst
      nameLast
      jobTitle
      hasHandbookUpdates
      hasFilledProfile
      hasTimesheetDue
      photoUrl(size: small)
      isPartTime
      isManager
      manager {
        id
        nameFirst
        nameLast
        email
      }
      tracksPTO
      office {
        OfficeID
        Name
      }
      department {
        id
        DepartmentID
        Name
        IsDirectoryRestricted
      }
      permissionKeys
      isDirectoryRestricted
      accountServicesTeams {
        id
        permissionType
        projectAccountID
      }
    }
    featureFlagListV2 {
      id
      name
      description
      active
    }
  }
`;

interface IPortalContext {
  children: React.ReactNode | Array<React.ReactNode>;
  env: envType;
  name: string;
  version: string;
  /**
   * The permissions the app itself requires. ANY one of them admits — this is a door.
   *
   * `permissions={[]}` used to OPEN the app to everybody: the prop was truthy, so the check ran, and
   * `hasPermissions([])` returned true. The list is non-empty by type now, and `hasPermissions` refuses
   * an empty one anyway. An app with no door still omits the prop, which is the only spelling of that.
   */
  permissions?: NonEmptyPermissions<PermissionKey>;
  bypassHandbookCheck?: boolean; // for use on the handbook itself
  bypassEditProfileCheck?: boolean; // for use on the profile itself
  bypassTimesheetCheck?: boolean; // for use on the timesheet itself
}

// The cached `me` an app renders from while USER_ME is in flight. Bump this whenever USER_ME's shape
// changes, so a blob of the old shape is ignored rather than read: one written before `permissionKeys`
// existed holds no key, and every app door showed the no-access page until the query landed.
const PORTAL_ME_VERSION = 2;

const PORTAL_ME_KEY = 'portalMe';

// Storage can be blocked or full, and a blob can be corrupt. Either way the app goes without the cache
// rather than failing to load: this runs at import, so a throw here is a blank page.
const readCachedMe = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(PORTAL_ME_KEY) ?? 'null') as { v: number; exp: number } | null;

    return cached?.v === PORTAL_ME_VERSION && cached.exp > dayjs().unix() ? cached : null;
  } catch {
    return null;
  }
};

const cookieMeObj = readCachedMe();

const PortalContext = (props: IPortalContext) => {
  const posthog = usePostHog();

  if (props.env && props.env !== 'production') {
    console.info('This application is running in env mode: ' + props.env);
  }

  const { loading, error, data } = useQuery(USER_ME, {
    onCompleted: (data) => {
      if (data?.me) {
        const tomorrow = dayjs().add(1, 'day').unix();
        // Replaces the whole blob, whatever shape the old one had. A failed write costs only the cache.
        try {
          localStorage.setItem(
            PORTAL_ME_KEY,
            JSON.stringify({ me: data.me, featureFlagListV2: data.featureFlagListV2, exp: tomorrow, v: PORTAL_ME_VERSION }),
          );
        } catch (storageError) {
          console.warn('Could not cache the signed-in profile', storageError);
        }
      }
    },
  });

  useEffect(() => {
    if (data) {
      // Identify sends an event, so you want may want to limit how often you call it
      posthog?.identify(`${data.me.id}`, {
        email: data.me.email,
        name: `${data.me.nameFirst} ${data.me.nameLast}`,
      });
      posthog?.group('department', data.me.department.id);
      posthog?.group('office', data.me.office.OfficeID);
    }
  }, [posthog, data?.me.id]);

  if (error) {
    // @ts-expect-error the type returned from apollo is not correct
    const isAuthError = error.networkError?.statusCode === 401;
    // @ts-expect-error: apollo router attaches status to error.extensions which is an any type
    const isApolloRouterAuthError = error?.graphQLErrors.some((error) => error?.extensions?.http?.status === 401);
    if (isAuthError || isApolloRouterAuthError) {
      redirectToLogin(props.env);
      return <LogoLoader />;
    } else {
      console.error(error);
      console.error(error.networkError);
      // @ts-expect-error the type returned from apollo is not correct
      return <ErrorDisplay title={`Server Error: ${error.networkError?.statusCode}`} error={error} />;
    }
  }

  if (!loading || cookieMeObj) {
    // Loading is complete
    // TODO: stop infinite auth loop and show perm error after number of retries
    if ((!data?.me || !data?.me.email) && !loading) {
      // User is not logged in; move them to them to the login page
      redirectToLogin(props.env);
      return <LogoLoader />;
    } else if (!props.bypassHandbookCheck && data?.me?.hasHandbookUpdates) {
      redirectToHandbook(props.env);
      return <LogoLoader />;
    } else if (!props.bypassEditProfileCheck && data?.me?.hasFilledProfile === false) {
      redirectToEditProfile(props.env, data.me.id);
    } else if (!props.bypassTimesheetCheck && data?.me?.hasTimesheetDue) {
      redirectToTimesheet(props.env);
      return <LogoLoader />;
    }
  } else {
    return <LogoLoader />;
  }

  //Set the config for the user
  const finalConfig = { ...config } as PortalConfigType;

  finalConfig.urls = props.env ? (config.urls[props.env] ?? config.urls) : config.urls;

  const ctxUser = userObj(data || cookieMeObj, props.env || 'production', finalConfig, props.name, props.version, !data);

  // User must have permission to view the app
  if (props.permissions) {
    const url = ctxUser.config.urls.portal ?? 'https://kgportal.com';

    // Refuse only on the live answer. The cached blob can be a day old, so somebody granted the door
    // since would otherwise see the no-access page on every load until the query lands.
    if (!ctxUser.hasPermissions(props.permissions)) {
      return loading ? <LogoLoader /> : <NoPermission href={url} />;
    }
  }

  return (
    <BaseCtx.Provider value={ctxUser}>
      <Commander />
      {props.children}
    </BaseCtx.Provider>
  );
};

export default ApolloContext;

export const useUser = () => {
  return useContext(BaseCtx);
};

export const useEnv = () => {
  const usr = useContext(BaseCtx);

  return usr.env;
};

export const useConfig = (): PortalConfigType => {
  const usr = useContext(BaseCtx);

  if (!usr || !usr.config) {
    return config;
  }

  return usr.config;
};

/**
 * Whether the current user satisfies a permission requirement.
 *
 * `NO_PERMISSION_ARM` is accepted and means THERE IS NO REQUIREMENT TO CHECK — a component whose
 * permission prop is optional passes it when the prop is absent. It returns false, so the arm
 * contributes nothing to a disjunction, and it logs nothing, because an absent arm is not a caller bug.
 * An EMPTY LIST is the caller bug and is a different thing: it does not compile, and `hasPermissions`
 * refuses and logs one that arrives through a variable.
 *
 * A component with an optional arm must still ask whether the arm is THERE — see `ClientPagePermission`.
 * `false` is the neutral element of a disjunction but not of a conjunction, so this hook cannot answer
 * both and does not try.
 *
 * Answered during render, never from an effect: a hook that starts at `false` hands every caller's first
 * render a refusal, and a page that guards on it drew the no-access page on every load. While the user
 * is still the cached one (`isProvisional`) the answer can be out of date; `NoPermission` covers that.
 */
export const usePermissions = (
  permissionKeys: PermissionKey | NonEmptyPermissions<PermissionKey> | typeof NO_PERMISSION_ARM,
  allRequired?: boolean,
) => {
  const usr = useContext(BaseCtx);

  if (permissionKeys === NO_PERMISSION_ARM || !usr?.hasPermissions) {
    return false;
  }

  return usr.hasPermissions(permissionKeys, allRequired);
};

/**
 * Whether the current user sees only themselves in the directory. Not a permission check — the
 * restriction is a property of their department, resolved server-side; see userObj.
 */
export const useIsDirectoryRestricted = () => {
  const usr = useContext(BaseCtx);

  return !!usr?.isDirectoryRestricted;
};

/**
 * THE TWO CLIENT-ACCESS HOOKS, replacing `useClientPermissions(accountId, permissionLevels?)`.
 *
 * One hook per question, mirroring `userObj` and the server's `clientPermissionGate.ts`. The levels array
 * is gone: it offered a combinatorial space in which exactly two points were ever used, and OMITTING it
 * admitted every employee on any real account.
 *
 * `accountId === undefined` still refuses — that is the "the row has not loaded yet" case and it is not
 * the same as account 0. Answered during render, for the reason `usePermissions` gives.
 */
const useClientAccess = (accountId: number | undefined, required: ClientAccessRequirement) => {
  const usr = useContext(BaseCtx);

  // The company-wide `full` tier first and `undefined` second, in that ORDER, because that is the order
  // the hook this replaces used: somebody with reach into every client passes before the account is
  // looked at, and an account that has not loaded yet refuses rather than being read as account 0.
  //
  // It was the legacy name `crm.admin`. `legacy_permission_keys` maps that name AND `portal.superadmin`
  // onto this key, so the one active person holding the second and not the first now passes here too —
  // in the not-yet-loaded window only, and `clientAccessLevel` has always answered `full` for them once
  // an account id arrives.
  if (usr?.hasPermissions?.(UNASSIGNED_CLIENTS_FULL_KEY)) {
    return true;
  }

  if (accountId === undefined || !usr?.hasFullClientAccess) {
    return false;
  }

  return required === 'full' ? usr.hasFullClientAccess(accountId) : usr.hasAtLeastLimitedClientAccess(accountId);
};

/** `full` on this account — write access. */
export const useFullClientAccess = (accountId: number | undefined) => useClientAccess(accountId, 'full');

/** `full` OR `limited` — any reach at all. Never "exactly limited". */
export const useAtLeastLimitedClientAccess = (accountId: number | undefined) =>
  useClientAccess(accountId, 'atLeastLimited');

interface IPermissions {
  children: JSX.Element | Array<JSX.Element>;
  /** `NO_PERMISSION_ARM` for a wrapper with no requirement of its own — it renders its children. */
  name: PermissionKey | NonEmptyPermissions<PermissionKey> | typeof NO_PERMISSION_ARM;
  all?: boolean | undefined;
  not?: boolean | undefined;
  hide?: boolean | undefined;
}

// Provides a helper for hiding/showing based on permission
export const Permission = (props: IPermissions): JSX.Element => {
  const flg = usePermissions(props.name, props.all);
  if (props.hide) {
    return <></>;
  }

  // `NO_PERMISSION_ARM` means there is nothing to satisfy, so the wrapper renders. This is a CONJUNCTION
  // — "render if allowed" — and true is its neutral element, which is why the marker reads as a pass
  // here and as a non-contribution in `ClientPagePermission`'s disjunction. It is also what the callers
  // that used to pass `[]` relied on: a nav item or a profile link with no permission of its own showed
  // to everybody, deliberately, and an empty list happened to say that.
  const allowed = props.name === NO_PERMISSION_ARM || flg;

  if (props.not) {
    return allowed ? <></> : <>{props.children}</>;
  }

  return allowed ? <>{props.children}</> : <></>;
};

interface IClientPermissions {
  children: JSX.Element | Array<JSX.Element>;
  accountId: number | undefined;
  /**
   * Which tier of client access admits. A COMPONENT cannot be two functions, so the requirement is
   * carried as a value — the same two points, and `'limited'` alone is still not one of them.
   */
  clientAccess: ClientAccessRequirement;
  hide?: boolean | undefined;
}

// Helper for hiding/showing based on Client Permissions
export const ClientPermission = (props: IClientPermissions): JSX.Element => {
  let flg = false;
  const usr = useContext(BaseCtx);

  // The second arm is what keeps a company-wide holder's chrome on a row with NO account — a contact
  // with no client, say, where the call site passes `accountId ?? 0`. It reads the key rather than
  // legacy `crm.admin` for the reason `useClientAccess` gives; the same one person moves, in the same
  // direction, and nobody is refused who was not refused before.
  if (props.accountId || usr.hasPermissions(UNASSIGNED_CLIENTS_FULL_KEY)) {
    flg = useClientAccess(props.accountId, props.clientAccess);
  }

  if (props.hide) {
    return <></>;
  }

  return flg ? <>{props.children}</> : <></>;
};

/**
 *
 * @param {string} featureFlagName The name of the feature flag that you want to use.
 * @param {Object} allRequired The current context feature flags.
 * @returns {Boolean} `true` If the current user/context has the feature flag enabled, `false` if not
 */
export const useFeatureFlag = (featureFlagName: string | Array<string>, allRequired?: boolean) => {
  const usr = useContext(BaseCtx);

  // During render, for the reason `usePermissions` gives: a flag that gates a page drew its refusal first.
  return !!usr?.hasFlags?.(featureFlagName, allRequired);
};

interface IFeatureFlag {
  children: React.ReactNode | Array<React.ReactNode>;
  name: string | Array<string>;
  all?: boolean | undefined;
  not?: boolean | undefined;
  hide?: boolean | undefined;
}

// Feature flag helper wrapper
export const FeatureFlag = (props: IFeatureFlag) => {
  const flg = useFeatureFlag(props.name, props.all);

  if (props.hide) {
    return <></>;
  }

  if (props.not) {
    return flg ? <></> : <>{props.children}</>;
  }

  return flg ? <>{props.children}</> : <></>;
};

export const useUserLocation = (geoLookup) => {
  const usr = useUser();
  const knownOffice = usr && usr.details
    ? locations.find((location) => location.OfficeID === usr?.details?.office?.OfficeID)
    : null;
  const homeOffice: TLocation | null = knownOffice ?? (
    usr?.details?.office?.OfficeID
      ? { Name: usr.details.office.Name ?? '', OfficeID: usr.details.office.OfficeID, latitude: 0, longitude: 0 }
      : null
  );
  const [location, setLocation] = useState(null as TLocation | null);

  function getLocation() {
    getCurrentLocation()
      .then((position: any) => {
        const geoOffice = getClosestOffice(position.coords.latitude, position.coords.longitude);

        setLocation(geoOffice);
      })
      .catch((err) => {
        // will go here if gelocation is disabled by user
        console.warn('could not geolocate user', err);
      });
  }

  useEffect(() => {
    if (geoLookup) {
      getLocation();
    }
  }, [geoLookup]); // In case our needs change

  return location || homeOffice; // Using the or, becausse an actual location beats thie home office
};
